import React, { useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./index.scss";

const DirectBillMaster = () => {
  const navigate = useNavigate();
  const { mode } = useParams();
  const location = useLocation();
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  // Form states
  const [configCode, setConfigCode] = useState(isEditMode ? "DBL-001" : "");
  const [configName, setConfigName] = useState(isEditMode ? "Standard Direct Bill" : "");
  const [isActive, setIsActive] = useState(true);
  const [billMethod, setBillMethod] = useState("Policy-wise");
  const [billFrequency, setBillFrequency] = useState("Monthly");
  const [paymentDueDays, setPaymentDueDays] = useState(30);
  const [gracePeriod, setGracePeriod] = useState(15);
  const [applyLateCharges, setApplyLateCharges] = useState(true);
  const [lateChargeType, setLateChargeType] = useState("Percentage");
  const [lateChargeValue, setLateChargeValue] = useState(2.5);
  const [minLateCharge, setMinLateCharge] = useState(10);

  // Insurer setup data
  const [insurers, setInsurers] = useState([
    {
      id: 1,
      insurerCode: "ABC001",
      insurerName: "ABC Insurance Co",
      billMethod: "Direct Bill",
      paymentTerms: 30,
      creditLimit: 500000,
      contactEmail: "billing@abcinsurance.com",
      autoGenerate: true,
      active: true
    },
    {
      id: 2,
      insurerCode: "XYZ002",
      insurerName: "XYZ Life Insurance",
      billMethod: "Account Current",
      paymentTerms: 45,
      creditLimit: 750000,
      contactEmail: "accounts@xyzlife.com",
      autoGenerate: false,
      active: true
    },
    {
      id: 3,
      insurerCode: "GHI003",
      insurerName: "Global Health Insurance",
      billMethod: "Statement",
      paymentTerms: 30,
      creditLimit: 300000,
      contactEmail: "remittance@globalhealth.com",
      autoGenerate: true,
      active: true
    }
  ]);

  // GL Accounts
  const [receivableAccount, setReceivableAccount] = useState("1200-001");
  const [premiumAccount, setPremiumAccount] = useState("4100-001");
  const [lateChargeAccount, setLateChargeAccount] = useState("4200-001");
  const [badDebtAccount, setBadDebtAccount] = useState("5100-001");

  const billMethodOptions = [
    { label: "Policy-wise", value: "Policy-wise" },
    { label: "Insured-wise", value: "Insured-wise" },
    { label: "Period-wise", value: "Period-wise" },
    { label: "On-Demand", value: "On-Demand" }
  ];

  const frequencyOptions = [
    { label: "Monthly", value: "Monthly" },
    { label: "Quarterly", value: "Quarterly" },
    { label: "Semi-Annual", value: "Semi-Annual" },
    { label: "Annual", value: "Annual" }
  ];

  const lateChargeOptions = [
    { label: "Flat Amount", value: "Flat Amount" },
    { label: "Percentage", value: "Percentage" },
    { label: "Tiered", value: "Tiered" }
  ];

  const handleSave = () => {
    console.log("Saving Direct Bill Master configuration");
    navigate("/master/finance/remittance");
  };

  const handleCancel = () => {
    navigate("/master/finance/remittance");
  };

  const editInsurerTemplate = (rowData) => {
    if (isViewMode) return rowData.active ? "Yes" : "No";
    return (
      <Checkbox
        checked={rowData.active}
        onChange={(e) => {
          const updatedInsurers = insurers.map(ins =>
            ins.id === rowData.id ? { ...ins, active: e.checked } : ins
          );
          setInsurers(updatedInsurers);
        }}
      />
    );
  };

  const autoGenerateTemplate = (rowData) => {
    if (isViewMode) return rowData.autoGenerate ? "Yes" : "No";
    return (
      <Checkbox
        checked={rowData.autoGenerate}
        onChange={(e) => {
          const updatedInsurers = insurers.map(ins =>
            ins.id === rowData.id ? { ...ins, autoGenerate: e.checked } : ins
          );
          setInsurers(updatedInsurers);
        }}
      />
    );
  };

  return (
    <div className="direct-bill-master">
      <div className="header-section">
        <h2>Direct Bill Processing Master - {mode?.charAt(0).toUpperCase() + mode?.slice(1)}</h2>
      </div>

      <Card>
        <TabView>
          <TabPanel header="Configuration">
            <div className="form-section">
              <h3>Direct Bill Settings</h3>
              <div className="grid">
                <div className="col-12 md:col-4">
                  <label>Config Code *</label>
                  <InputText
                    value={configCode}
                    onChange={(e) => setConfigCode(e.target.value)}
                    disabled={isViewMode}
                    placeholder="DBL-{000}"
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-4">
                  <label>Config Name *</label>
                  <InputText
                    value={configName}
                    onChange={(e) => setConfigName(e.target.value)}
                    disabled={isViewMode}
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-4">
                  <label>Active</label>
                  <div className="checkbox-container">
                    <Checkbox
                      checked={isActive}
                      onChange={(e) => setIsActive(e.checked)}
                      disabled={isViewMode}
                    />
                    <span className="ml-2">Active</span>
                  </div>
                </div>
              </div>

              <h3 className="mt-4">Billing Parameters</h3>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label>Bill Generation Method</label>
                  <Dropdown
                    value={billMethod}
                    options={billMethodOptions}
                    onChange={(e) => setBillMethod(e.value)}
                    disabled={isViewMode}
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label>Bill Frequency</label>
                  <Dropdown
                    value={billFrequency}
                    options={frequencyOptions}
                    onChange={(e) => setBillFrequency(e.value)}
                    disabled={isViewMode}
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label>Payment Due Days</label>
                  <InputNumber
                    value={paymentDueDays}
                    onValueChange={(e) => setPaymentDueDays(e.value)}
                    disabled={isViewMode}
                    min={1}
                    max={90}
                    suffix=" days"
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label>Grace Period (Days)</label>
                  <InputNumber
                    value={gracePeriod}
                    onValueChange={(e) => setGracePeriod(e.value)}
                    disabled={isViewMode}
                    min={0}
                    max={30}
                    suffix=" days"
                    className="w-full"
                  />
                </div>
              </div>

              <h3 className="mt-4">Late Payment Settings</h3>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label>Apply Late Charges</label>
                  <div className="checkbox-container">
                    <Checkbox
                      checked={applyLateCharges}
                      onChange={(e) => setApplyLateCharges(e.checked)}
                      disabled={isViewMode}
                    />
                    <span className="ml-2">Apply Late Charges</span>
                  </div>
                </div>
                {applyLateCharges && (
                  <>
                    <div className="col-12 md:col-6">
                      <label>Late Charge Type</label>
                      <Dropdown
                        value={lateChargeType}
                        options={lateChargeOptions}
                        onChange={(e) => setLateChargeType(e.value)}
                        disabled={isViewMode}
                        className="w-full"
                      />
                    </div>
                    <div className="col-12 md:col-6">
                      <label>Late Charge Amount/Rate</label>
                      <InputNumber
                        value={lateChargeValue}
                        onValueChange={(e) => setLateChargeValue(e.value)}
                        disabled={isViewMode}
                        prefix={lateChargeType === "Percentage" ? "" : "$"}
                        suffix={lateChargeType === "Percentage" ? "%" : ""}
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        className="w-full"
                      />
                    </div>
                    <div className="col-12 md:col-6">
                      <label>Minimum Late Charge</label>
                      <InputNumber
                        value={minLateCharge}
                        onValueChange={(e) => setMinLateCharge(e.value)}
                        disabled={isViewMode}
                        mode="currency"
                        currency="USD"
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Insurer Setup">
            <div className="insurer-setup-section">
              <div className="toolbar mb-3">
                <Button
                  label="Add Insurer"
                  icon="pi pi-plus"
                  className="p-button-primary mr-2"
                  disabled={isViewMode}
                />
                <Button
                  label="Import"
                  icon="pi pi-upload"
                  className="p-button-secondary"
                  disabled={isViewMode}
                />
              </div>

              <DataTable value={insurers} className="insurer-grid">
                <Column field="insurerCode" header="Insurer Code" style={{ width: "10%" }} />
                <Column field="insurerName" header="Insurer Name" style={{ width: "20%" }} />
                <Column field="billMethod" header="Bill Method" style={{ width: "12%" }} />
                <Column field="paymentTerms" header="Payment Terms" style={{ width: "10%" }}
                  body={(rowData) => `${rowData.paymentTerms} days`} />
                <Column field="creditLimit" header="Credit Limit" style={{ width: "12%" }}
                  body={(rowData) => `$${rowData.creditLimit.toLocaleString()}`} />
                <Column field="contactEmail" header="Contact Email" style={{ width: "15%" }} />
                <Column field="autoGenerate" header="Auto Generate" style={{ width: "8%" }}
                  body={autoGenerateTemplate} />
                <Column field="active" header="Active" style={{ width: "8%" }}
                  body={editInsurerTemplate} />
                {!isViewMode && (
                  <Column header="" style={{ width: "5%" }}
                    body={() => (
                      <Button icon="pi pi-trash" className="p-button-text p-button-danger" />
                    )}
                  />
                )}
              </DataTable>
            </div>
          </TabPanel>

          <TabPanel header="GL Mapping">
            <div className="gl-mapping-section">
              <h3>Direct Bill GL Configuration</h3>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label>Receivable Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={receivableAccount}
                      onChange={(e) => setReceivableAccount(e.target.value)}
                      disabled={isViewMode}
                      className="w-full"
                    />
                    <Button icon="pi pi-search" disabled={isViewMode} />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <label>Premium Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={premiumAccount}
                      onChange={(e) => setPremiumAccount(e.target.value)}
                      disabled={isViewMode}
                      className="w-full"
                    />
                    <Button icon="pi pi-search" disabled={isViewMode} />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <label>Late Charge Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={lateChargeAccount}
                      onChange={(e) => setLateChargeAccount(e.target.value)}
                      disabled={isViewMode}
                      className="w-full"
                    />
                    <Button icon="pi pi-search" disabled={isViewMode} />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <label>Bad Debt Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={badDebtAccount}
                      onChange={(e) => setBadDebtAccount(e.target.value)}
                      disabled={isViewMode}
                      className="w-full"
                    />
                    <Button icon="pi pi-search" disabled={isViewMode} />
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>

        <div className="action-buttons mt-4">
          {!isViewMode && (
            <Button
              label="Save"
              icon="pi pi-save"
              className="p-button-primary mr-2"
              onClick={handleSave}
            />
          )}
          <Button
            label={isViewMode ? "Close" : "Cancel"}
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={handleCancel}
          />
        </div>
      </Card>
    </div>
  );
};

export default DirectBillMaster;